<script setup lang="ts">
import { computed } from 'vue';
import CopilotConfigBubbleSvg from '@/assets/svg/copilot-configuration-bubble.svg?component';
import ChevronDownSvg from '@/assets/svg/chevron-down.svg?component';

const props = defineProps<{
  displayName?: string;
  ownerName: string;
  isCreatedByCurrentUser?: boolean;
  isDefault?: boolean;
  /** The template has no owner and is maintained by the employer's admins. */
  isTeamOwned?: boolean;
}>();

const displayName = computed(() => props.displayName ?? 'Copilot');

const createdByText = computed(() => {
  if (props.isDefault) {
    return `Created by FactoryFix`;
  }

  if (props.isTeamOwned) {
    return 'Owned by your team';
  }

  if (props.isCreatedByCurrentUser) {
    return `Owned by Me (${props.ownerName})`;
  }

  // Person-owned but the owner is no longer an employer user: with the ownership copy on, say
  // so (same wording as the My AI Recruiting Team card) instead of implying it is the team's.
  if (!props.ownerName || props.ownerName === 'Unknown') {
    return 'Owned by a former teammate';
  }

  return `Owned by ${props.ownerName}`;
});
</script>

<template>
  <div
    v-bind="$attrs"
    class="flex w-full cursor-pointer items-center justify-between rounded-md bg-white p-4 outline outline-1 outline-tint-80 transition-all duration-200 hover:outline-2 hover:outline-highlight-600"
  >
    <div class="flex items-center">
      <!-- Copilot Config Bubble -->
      <div class="-mt-4 flex items-center justify-center">
        <CopilotConfigBubbleSvg width="24" height="24" />
      </div>

      <!-- Name -->
      <div class="ml-2">
        <div class="flex items-center">
          <div class="text-lg font-extrabold text-black">{{ displayName }}</div>
          <span
            v-if="isDefault"
            class="ml-2 inline-block rounded-[40px] bg-primary-100 px-2 py-1 text-xs font-bold leading-[100%] text-primary-600"
          >
            FactoryFix Default
          </span>
        </div>
        <div
          class="text-xs font-normal text-shade-900"
          data-test="copilot-personality-selection-card-owner"
        >
          {{ createdByText }}
        </div>
      </div>
    </div>

    <!-- Select Button -->
    <button
      class="border-shade-200 hover:bg-shade-50 flex h-9 items-center rounded px-3 text-sm font-medium text-shade-900 transition-colors duration-200"
    >
      <span class="text-xs font-bold text-shade-800">Select</span>
      <ChevronDownSvg class="ml-1" />
    </button>
  </div>
</template>
